import { useEffect, useMemo, useState } from "react";
import { ArrowRightLeft, FileVideo, Filter, Play, Search, Trash2, X } from "lucide-react";
import {
  deleteMediaAsset,
  getAdminMediaPreview,
  listAdminMediaCatalog,
  moveLessonVideo,
  type AdminLearningTreeMenu,
  type AdminMediaCatalogVideo,
} from "./api";

type Props = {
  tree: AdminLearningTreeMenu[];
  onChanged: () => Promise<void>;
};

type PreviewUrl = { url: string; expiresAt: number };

function errorText(cause: unknown): string {
  return cause instanceof Error ? cause.message : "Something went wrong. Please try again.";
}

function formatBytes(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatTime(seconds: number | null): string {
  if (!seconds) return "Duration pending";
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remaining = seconds % 60;
  return hours > 0 ? `${hours}:${String(minutes).padStart(2, "0")}:${String(remaining).padStart(2, "0")}` : `${minutes}:${String(remaining).padStart(2, "0")}`;
}

function canPreview(video: AdminMediaCatalogVideo): boolean {
  return video.status === "ready" || video.status === "reviewed";
}

function Thumbnail({ video, preview, isPlaying, onPlay }: { video: AdminMediaCatalogVideo; preview: PreviewUrl | undefined; isPlaying: boolean; onPlay: () => void }) {
  if (!preview) return <div className="catalog-thumbnail catalog-thumbnail-placeholder"><FileVideo size={28} /><span>{video.status === "uploading" ? "Uploading" : "Preview unavailable"}</span></div>;
  return <div className="catalog-thumbnail">
    <video
      src={preview.url}
      muted={!isPlaying}
      controls={isPlaying}
      playsInline
      preload="metadata"
      onLoadedMetadata={(event) => { if (!isPlaying && event.currentTarget.currentTime === 0) event.currentTarget.currentTime = 0.1; }}
    />
    {!isPlaying && <button type="button" className="catalog-preview-button" onClick={onPlay}><Play size={15} />Preview</button>}
  </div>;
}

export function AdminVideoCatalog({ tree, onChanged }: Props) {
  const [videos, setVideos] = useState<AdminMediaCatalogVideo[]>([]);
  const [previewUrls, setPreviewUrls] = useState<Record<string, PreviewUrl>>({});
  const [query, setQuery] = useState("");
  const [menuId, setMenuId] = useState("");
  const [courseId, setCourseId] = useState("");
  const [chapterId, setChapterId] = useState("");
  const [status, setStatus] = useState("");
  const [placement, setPlacement] = useState<"" | "attached" | "unassigned">("");
  const [moveVideo, setMoveVideo] = useState<AdminMediaCatalogVideo | null>(null);
  const [deleteVideo, setDeleteVideo] = useState<AdminMediaCatalogVideo | null>(null);
  const [targetMenuId, setTargetMenuId] = useState("");
  const [targetCourseId, setTargetCourseId] = useState("");
  const [targetChapterId, setTargetChapterId] = useState("");
  const [playingAssetId, setPlayingAssetId] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const refresh = async () => {
    setLoading(true);
    try { setVideos(await listAdminMediaCatalog()); }
    catch (cause) { setError(errorText(cause)); }
    finally { setLoading(false); }
  };

  useEffect(() => { void refresh(); }, []);

  const selectedMenu = useMemo(() => tree.find((item) => item.id === menuId) ?? null, [tree, menuId]);
  const selectedCourse = useMemo(() => selectedMenu?.courses.find((item) => item.id === courseId) ?? null, [selectedMenu, courseId]);
  useEffect(() => { setCourseId((current) => current && selectedMenu?.courses.some((item) => item.id === current) ? current : ""); }, [selectedMenu]);
  useEffect(() => { setChapterId((current) => current && selectedCourse?.chapters.some((item) => item.id === current) ? current : ""); }, [selectedCourse]);

  const targetMenu = useMemo(() => tree.find((item) => item.id === targetMenuId) ?? null, [tree, targetMenuId]);
  const targetCourse = useMemo(() => targetMenu?.courses.find((item) => item.id === targetCourseId) ?? null, [targetMenu, targetCourseId]);
  useEffect(() => { setTargetCourseId((current) => current && targetMenu?.courses.some((item) => item.id === current) ? current : targetMenu?.courses[0]?.id ?? ""); }, [targetMenu]);
  useEffect(() => { setTargetChapterId((current) => current && targetCourse?.chapters.some((item) => item.id === current) ? current : targetCourse?.chapters[0]?.id ?? ""); }, [targetCourse]);

  const filteredVideos = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase();
    return videos.filter((video) => {
      const searchable = [video.originalFilename, video.lessonVideoTitle, video.menuTitle, video.courseTitle, video.chapterTitle].filter(Boolean).join(" ").toLocaleLowerCase();
      return (!normalized || searchable.includes(normalized))
        && (!menuId || video.menuId === menuId)
        && (!courseId || video.courseId === courseId)
        && (!chapterId || video.chapterId === chapterId)
        && (!status || video.status === status)
        && (!placement || (placement === "attached" ? Boolean(video.lessonVideoId) : !video.lessonVideoId));
    });
  }, [videos, query, menuId, courseId, chapterId, status, placement]);

  const previewCandidates = useMemo(() => filteredVideos.filter(canPreview).slice(0, 18), [filteredVideos]);
  useEffect(() => {
    let cancelled = false;
    const fetchPreviews = async () => {
      const pending = previewCandidates.filter((video) => {
        const existing = previewUrls[video.mediaAssetId];
        return !existing || existing.expiresAt <= Date.now() + 60_000;
      });
      if (!pending.length) return;
      const pairs = await Promise.all(pending.map(async (video) => {
        try {
          const response = await getAdminMediaPreview(video.mediaAssetId);
          return [video.mediaAssetId, { url: response.playbackUrl, expiresAt: Date.now() + response.expiresInSeconds * 1000 }] as const;
        } catch { return null; }
      }));
      const resolved = pairs.filter((pair) => pair !== null) as Array<readonly [string, PreviewUrl]>;
      if (!cancelled) setPreviewUrls((current) => ({ ...current, ...Object.fromEntries(resolved) }));
    };
    void fetchPreviews();
    return () => { cancelled = true; };
  }, [previewCandidates, previewUrls]);

  const beginMove = (video: AdminMediaCatalogVideo) => {
    if (!video.lessonVideoId) return;
    setMoveVideo(video);
    setTargetMenuId(video.menuId ?? tree[0]?.id ?? "");
    setTargetCourseId(video.courseId ?? "");
    setTargetChapterId(video.chapterId ?? "");
    setError(""); setMessage("");
  };

  const move = async () => {
    if (!moveVideo?.lessonVideoId || !targetChapterId) return;
    setBusy(true); setError(""); setMessage("");
    try {
      await moveLessonVideo(moveVideo.lessonVideoId, targetChapterId);
      setMoveVideo(null);
      await Promise.all([refresh(), onChanged()]);
      setMessage(`${moveVideo.lessonVideoTitle ?? moveVideo.originalFilename} was moved to the selected chapter.`);
    } catch (cause) { setError(errorText(cause)); }
    finally { setBusy(false); }
  };

  const removeUnassignedVideo = async () => {
    if (!deleteVideo) return;
    setBusy(true); setError(""); setMessage("");
    try {
      await deleteMediaAsset(deleteVideo.mediaAssetId);
      setDeleteVideo(null);
      await Promise.all([refresh(), onChanged()]);
      setMessage(`${deleteVideo.originalFilename} was permanently deleted from private storage.`);
    } catch (cause) { setError(errorText(cause)); }
    finally { setBusy(false); }
  };

  return <section className="data-card video-catalog-card">
    <div className="panel-heading"><div><h2>Video catalog</h2><p>Search every upload, see where it belongs, preview its first frame, or move an attached lesson to another chapter.</p></div><Filter size={20} /></div>
    {message && <p className="catalog-feedback catalog-feedback-success">{message}</p>}
    {error && <p className="catalog-feedback catalog-feedback-error" role="alert">{error}</p>}
    <div className="catalog-filter-bar">
      <label className="catalog-search"><Search size={16} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search filename, video title, course, or chapter" /></label>
      <label>Menu<select value={menuId} onChange={(event) => setMenuId(event.target.value)}><option value="">All menus</option>{tree.map((menu) => <option key={menu.id} value={menu.id}>{menu.title}</option>)}</select></label>
      <label>Course<select value={courseId} disabled={!menuId} onChange={(event) => setCourseId(event.target.value)}><option value="">All courses</option>{selectedMenu?.courses.map((course) => <option key={course.id} value={course.id}>{course.title}</option>)}</select></label>
      <label>Chapter<select value={chapterId} disabled={!courseId} onChange={(event) => setChapterId(event.target.value)}><option value="">All chapters</option>{selectedCourse?.chapters.map((chapter) => <option key={chapter.id} value={chapter.id}>{chapter.title}</option>)}</select></label>
      <label>Status<select value={status} onChange={(event) => setStatus(event.target.value)}><option value="">All statuses</option><option value="uploading">Uploading</option><option value="ready">Needs duration</option><option value="reviewed">Ready / attached</option><option value="failed">Failed</option></select></label>
      <label>Placement<select value={placement} onChange={(event) => setPlacement(event.target.value as "" | "attached" | "unassigned")}><option value="">Attached and unassigned</option><option value="attached">Attached only</option><option value="unassigned">Unassigned only</option></select></label>
    </div>
    <p className="catalog-count">{loading ? "Loading videos…" : `${filteredVideos.length} of ${videos.length} video${videos.length === 1 ? "" : "s"} shown`}</p>
    <div className="video-catalog-grid">
      {filteredVideos.map((video) => <article className="video-catalog-item" key={video.mediaAssetId}>
        <Thumbnail video={video} preview={previewUrls[video.mediaAssetId]} isPlaying={playingAssetId === video.mediaAssetId} onPlay={() => setPlayingAssetId(video.mediaAssetId)} />
        <div className="video-catalog-copy"><div className="catalog-title-row"><strong>{video.lessonVideoTitle ?? video.originalFilename}</strong><span className={`status-tag ${video.status}`}>{video.lessonVideoId ? "attached" : video.status === "reviewed" ? "ready to attach" : video.status}</span></div><small title={video.originalFilename}>{video.originalFilename}</small><span>{formatBytes(video.sizeBytes)} · {formatTime(video.durationSeconds)}</span><div className="catalog-placement"><span><b>Menu:</b> {video.menuTitle ?? "Not attached"}</span><span><b>Course:</b> {video.courseTitle ?? "—"}</span><span><b>Chapter:</b> {video.chapterTitle ?? "—"}</span></div></div>
        <div className="catalog-card-actions">{video.lessonVideoId
          ? <button type="button" className="secondary-button compact-button" disabled={busy} onClick={() => beginMove(video)}><ArrowRightLeft size={15} />Move</button>
          : deleteVideo?.mediaAssetId === video.mediaAssetId
            ? <div className="catalog-delete-confirmation" role="alert"><span>{video.status === "uploading" ? "Cancel this upload?" : "Delete permanently?"}</span><button type="button" className="danger-button compact-button" disabled={busy} onClick={() => void removeUnassignedVideo()}>{video.status === "uploading" ? "Cancel upload" : "Delete"}</button><button type="button" className="secondary-button compact-button" disabled={busy} onClick={() => setDeleteVideo(null)}>Keep</button></div>
            : video.status === "validating"
              ? <span className="catalog-unassigned">Preparing web version</span>
              : <div className="catalog-unassigned-actions"><span className="catalog-unassigned">Not attached</span><button type="button" className="danger-button compact-button" disabled={busy} onClick={() => setDeleteVideo(video)}><Trash2 size={15} />{video.status === "uploading" ? "Cancel" : "Delete"}</button></div>}</div>
      </article>)}
      {!loading && filteredVideos.length === 0 && <p className="empty-inline">No video matches the selected filters.</p>}
    </div>
    {moveVideo && <div className="catalog-move-panel" role="dialog" aria-label="Move video lesson">
      <div><strong>Move: {moveVideo.lessonVideoTitle ?? moveVideo.originalFilename}</strong><p>Select the new main menu, course, and chapter. The video URL remains stable while its placement changes.</p></div>
      <div className="catalog-move-fields"><label>Main menu<select value={targetMenuId} onChange={(event) => setTargetMenuId(event.target.value)}><option value="">Select main menu</option>{tree.map((menu) => <option key={menu.id} value={menu.id}>{menu.title}</option>)}</select></label><label>Course<select value={targetCourseId} disabled={!targetMenu} onChange={(event) => setTargetCourseId(event.target.value)}><option value="">Select course</option>{targetMenu?.courses.map((course) => <option key={course.id} value={course.id}>{course.title}</option>)}</select></label><label>Chapter<select value={targetChapterId} disabled={!targetCourse} onChange={(event) => setTargetChapterId(event.target.value)}><option value="">Select chapter</option>{targetCourse?.chapters.map((chapter) => <option key={chapter.id} value={chapter.id}>{chapter.title}</option>)}</select></label></div>
      <div className="catalog-move-actions"><button type="button" className="primary-button" disabled={busy || !targetChapterId || targetChapterId === moveVideo.chapterId} onClick={() => void move()}><ArrowRightLeft size={16} />{busy ? "Moving…" : "Move video"}</button><button type="button" className="secondary-button" disabled={busy} onClick={() => setMoveVideo(null)}><X size={16} />Cancel</button></div>
    </div>}
  </section>;
}
